import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useQuery } from "@tanstack/react-query";
import { Module } from "@/constants";
import { ProjectService } from "@/modules/projects/service/project";
import PageHeader from "@/components/PageHeader";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import PageContent from "@/components/PageContent";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/Spinner";
import { useInvoiceSpecificationId } from "@/modules/projects/hooks/useInvoiceSpecificationId";
import { SummarySection } from "@/modules/projects/components/invoiceSpecifications/section/SummarySection";
import { LaborCostsInStoresSection } from "@/modules/projects/components/invoiceSpecifications/section/LaborCostsInStoresSection";
import { BasicDataForm } from "@/modules/projects/components/invoiceSpecifications/section/BasicDataForm";
import { invoiceSpecificationsService } from "@/services/invoiceSpecifications";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";

import { useToast } from "@/components/ui/use-toast";
import { useInvoiceSpecificationsEditorBreadcrumbs } from "@/modules/projects/components/invoiceSpecifications/hooks/useInvoiceSpecificationsEditorBreadcrumbs";
import { useDeleteInvoiceSpecification } from "@/modules/projects/components/invoiceSpecifications/hooks/useDeleteInvoiceSpecification";
import { useSettlementId } from "../../hooks/useSettlementId";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ProjectSettlementService } from "@/modules/projectSettlements/service/projectSettlements";
import {
  UpdateInvoiceSpecification,
  ReadInvoiceSpecification,
} from "@/types/invoiceSpecification";
import { CostsSection } from "./section/CostsSection";
import SaveButton from "@/components/SaveButton";
import { ExportButton } from "@/components/export/ExportButton";
import useAccount from "@/modules/account/hooks/useAccount";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import {
  getInvoiceSpecificationExportStorageKey,
  INVOICE_SPECIFICATION_EXPORT_COLUMN_CONFIG,
} from "./invoiceSpecificationExportConfig";
import { EXPORT_FORMAT_XLSX } from "@/constants";
import { DeleteButton } from "@/components/DeleteButton";

export default function ProjectInvoiceSpecificationsEditor() {
  const { toast } = useToast();
  const { account } = useAccount();
  const { can_export } = useModulePermissions(Module.InvoiceSpecifications);
  const routeProjectId = useProjectId();
  const settlementId = useSettlementId();
  const invoiceSpecificationId = useInvoiceSpecificationId();
  const { t } = useTranslation();

  const [projectId, setProjectId] = useState<string | undefined>(
    routeProjectId,
  );

  const projectQuery = useQuery({
    queryKey: [Module.Projects, projectId, "invoice-specifications"] as const,
    queryFn: () => ProjectService.get(projectId),
    enabled: Boolean(projectId),
  });

  const mutateInvoiceSpecificationBasicData = async (
    values: UpdateInvoiceSpecification,
  ): Promise<ReadInvoiceSpecification> =>
    invoiceSpecificationsService.patchBasicData(invoiceSpecificationId, values);

  const invoiceSpecificationQuery = useQuery({
    queryKey: [
      Module.InvoiceSpecifications,
      invoiceSpecificationId,
      "editor",
    ] as const,
    queryFn: () =>
      invoiceSpecificationsService.getInvoiceSpecification(
        invoiceSpecificationId,
      ),
  });

  const settlementQuery = useQuery({
    queryKey: [Module.ProjectSettlements, settlementId] as const,
    queryFn: () => ProjectSettlementService.get(settlementId),
    // run only when projectId is not provided from the route –
    // edit from project settlements directory
    enabled: !routeProjectId,
  });

  useEffect(() => {
    if (!routeProjectId && settlementQuery.data?.project_id) {
      setProjectId(settlementQuery.data.project_id);
    }
  }, [settlementQuery.data, routeProjectId]);

  const breadcrumbs = useInvoiceSpecificationsEditorBreadcrumbs({
    projectId: routeProjectId,
    settlementId,
    invoiceSpecificationQuery,
  });

  const invoiceSpecificationMutation = useMutationWithErrorHandling({
    mutationFn: (data: UpdateInvoiceSpecification) =>
      mutateInvoiceSpecificationBasicData(data),
    onSuccess: () => {
      toast({ title: t("projectInvoiceSpecifications.success.update") });
      invoiceSpecificationQuery.refetch();
    },
    errorTitle: t("projectInvoiceSpecifications.error.update"),
  });

  const deleteMutation = useDeleteInvoiceSpecification();

  const exportQueryParams = useMemo(
    () =>
      new URLSearchParams({
        invoice_specification_id: invoiceSpecificationId,
      }),
    [invoiceSpecificationId],
  );

  const exportFeatures = useMemo(
    () =>
      account?.id
        ? {
            columnSelection: {
              columns: INVOICE_SPECIFICATION_EXPORT_COLUMN_CONFIG,
              storageKey: getInvoiceSpecificationExportStorageKey(account.id),
              contextId: invoiceSpecificationId,
            },
          }
        : undefined,
    [account?.id, invoiceSpecificationId],
  );

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(undefined);
  }, [deleteMutation]);

  if (
    projectQuery.isLoading ||
    invoiceSpecificationQuery.isLoading ||
    !projectQuery.data
  ) {
    return (
      <Spinner
        label={t("projectInvoiceSpecifications.loading")}
        className="w-full h-full"
      />
    );
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />
      <PageContent
        title={t("projectInvoiceSpecifications.title.edit")}
        headerActions={[
          <ExportButton
            key="export"
            moduleName={Module.InvoiceSpecifications}
            queryParams={exportQueryParams}
            allowedFormats={[EXPORT_FORMAT_XLSX]}
            exportFeatures={exportFeatures}
            disabled={!can_export}
          />,
        ]}
      >
        <div className="flex flex-col gap-4">
          <ProjectDetailsAndNavigation projectId={projectId} />
          <div className="grid grid-cols-1 grid-rows-[auto_1fr] gap-4">
            {invoiceSpecificationQuery.isFetching && (
              <Spinner
                label={t("projectInvoiceSpecifications.loading")}
                className="w-full h-full"
              />
            )}
            {!invoiceSpecificationQuery.isFetching && (
              <BasicDataForm
                isEditMode
                onSubmit={invoiceSpecificationMutation.mutateAsync}
                values={invoiceSpecificationQuery.data}
                disabled={invoiceSpecificationQuery.isPending}
                isLoading={invoiceSpecificationMutation.isPending}
                projectName={projectQuery.data?.name}
                footer={
                  <div className="flex justify-end gap-2">
                    <DeleteButton
                      onClick={handleDelete}
                      disabled={
                        Boolean(invoiceSpecificationQuery.data?.deleted_at) ||
                        invoiceSpecificationQuery.isPending ||
                        deleteMutation.isPending
                      }
                      dialogContent={t(
                        "projectInvoiceSpecifications.delete.confirm",
                      )}
                    />
                    <SaveButton
                      isLoading={
                        invoiceSpecificationMutation.isPending ||
                        deleteMutation.isPending
                      }
                    />
                  </div>
                }
              />
            )}
            <SummarySection invoiceSpecificationId={invoiceSpecificationId} />
            <CostsSection invoiceSpecificationId={invoiceSpecificationId} />
            <LaborCostsInStoresSection
              invoiceSpecificationId={invoiceSpecificationId}
              projectDependsOn={projectQuery.data.project_depends_on}
            />
          </div>
        </div>
      </PageContent>
    </>
  );
}
